import { BRAND } from "@/lib/brand";
import { FileEdit, Send, ShieldCheck, Zap } from "lucide-react";

// Small helper so legacy entries that predate the workflow don't crash on
// missing status — they're treated as draft (same as the backend default).
export function entryStatus(entry) {
  return entry?.status || "draft";
}

// Mirrors the backend's _assert_editable: drafts and auto-approved entries
// (approved with no human reviewer) can be freely edited/deleted by the owner.
export function isEntryEditable(entry) {
  const s = entryStatus(entry);
  if (s === "draft") return true;
  if (s === "approved" && entry && !entry.approved_by) return true;
  return false;
}

// True if the freelancer should see a Submit button on this entry — only
// drafts can be submitted, and only in manual approval mode.
export function isEntrySubmittable(entry) {
  return entryStatus(entry) === "draft";
}

// "Approved" status with no `approved_by` came from auto-approve mode
// (no human reviewed it) — render it as its own variant so the visual matches
// the meaning.
function variantFor(entry, statusOverride) {
  const status = statusOverride || entryStatus(entry);
  if (status === "approved" && entry && !entry.approved_by) {
    return "auto";
  }
  return status;
}

const STYLES = {
  draft: {
    label: "Draft",
    bg: "#94a3b820", // slate
    color: "#475569",
    Icon: FileEdit,
  },
  submitted: {
    label: "Submitted",
    bg: "#f59e0b22", // amber
    color: "#b45309",
    Icon: Send,
  },
  approved: {
    label: "Approved",
    bg: `${BRAND.teal}22`,
    color: BRAND.tealDark,
    Icon: ShieldCheck,
  },
  auto: {
    label: "Auto",
    bg: `${BRAND.teal}15`,
    color: BRAND.tealDark,
    Icon: Zap,
  },
};

// Two ways to use this component:
//   <StatusBadge entry={entry} />   - infers status + auto-approved variant
//   <StatusBadge status="draft" />  - explicit override (e.g. legend rows)
export default function StatusBadge({ entry, status, size = "sm" }) {
  const variant = entry ? variantFor(entry) : status || "draft";
  const s = STYLES[variant] || STYLES.draft;
  const { Icon } = s;
  const px = size === "xs" ? "px-1.5 py-0" : "px-2 py-0.5";
  const text = size === "xs" ? "text-[10px]" : "text-xs";
  const iconSize = size === "xs" ? 10 : 11;
  return (
    <span
      className={`inline-flex items-center gap-1 ${text} font-semibold uppercase tracking-wider ${px} rounded-full whitespace-nowrap`}
      style={{ background: s.bg, color: s.color }}
    >
      <Icon size={iconSize} />
      {s.label}
    </span>
  );
}
